import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../../hook/useAuth";
import { useEffect, useRef, useState } from "react";
import { PleasewaitPopup } from "../../../components/PleasewaitPopup";
import useConfirm from "../../../hook/useConfirm";
import useDelete from "../../../hook/useDelete";
import axios from "axios";
import { baseURL } from "../../../config/api";
import { validateMin } from "../../../utils/validateMin";
import { iconValues } from "../../../utils/iconsValues";
import { inventoryTypes } from "../../../utils/inventoryType";


export const EditServiceType = () => {
    const navigate = useNavigate();
    const idNumber = useLocation();
    const { auth }: any = useAuth();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const { setDeletePopup, setData: setDeleteData }: any = useDelete();
    const [serviceName, setServiceName] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [price, setPrice] = useState<number>(0);
    const [serviceNumber, setServiceNumber] = useState<string>("");
    const [requiresTireCount, setRequiresTireCount] = useState<string>("Yes");
    const [icon, setIcon] = useState<string>("");
    const [inventoryType, setInventoryType] = useState<number>(0);
    const [isTaxable, setIsTaxable] = useState<boolean>(false);
    const [serviceTaxRate, setServiceTaxRate] = useState<number>(0);
    const [estimatedMinutes, setEstimatedMinutes] = useState<number>(0);
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    const selected = iconValues.find((o: any) => o.value === icon);

    // Close on outside click
    useEffect(() => {
        const handler = (e: any) => {
            if (!ref.current?.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        setPleaseWaitPopup(true);
        axios
            .get(`${baseURL}services/${idNumber?.state}`)
            .then((response) => {
                setServiceName(response?.data?.serviceName);
                setDescription(response?.data?.description);
                setPrice(response?.data?.price);
                setServiceNumber(response?.data?.serviceNumber);
                setRequiresTireCount(response?.data?.requiresTireCount ? "Yes" : "No");
                setIcon(response?.data?.icon);
                setInventoryType(response?.data?.inventoryType);
                setIsTaxable(response?.data?.isTaxable);
                setServiceTaxRate(response?.data?.serviceTaxRate);
                setEstimatedMinutes(response?.data?.estimatedMinutes);
                setPleaseWaitPopup(false);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNumber]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setPleaseWaitPopup(true);

        await axios
            .put(
                `${baseURL}services?userId=${auth?.id}`,
                JSON.stringify({
                    serviceID: idNumber?.state,
                    serviceName,
                    description,
                    price,
                    serviceNumber,
                    icon,
                    requiresTireCount: requiresTireCount === "Yes" ? true : false,
                    isTaxable,
                    serviceTaxRate,
                    inventoryType,
                    estimatedMinutes
                }),
                {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: false,
                }
            )
            .then((response) => {
                setPleaseWaitPopup(false);
                navigate(-1);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
                console.error("Error:", error);
            });
    }

    const deletingType = async (id: number) => {
        setDeleteData({
            id: id,
            title: `Delete User`,
            summary: `Are you sure you want to delete this type?`,
            isDelete: (callback: any) => {
                setPleaseWaitPopup(true);
                callback();

                axios
                    .delete(`${baseURL}services?id=${id}&userId=${auth?.id}`)
                    .then(() => {
                        setPleaseWaitPopup(false);
                        navigate(-1);
                    })
                    .catch((error) => {
                        setConfirmData({
                            title: "Warning",
                            summary: "Delete failed.",
                            confirmBtn: "Ok",
                            isConfirm: (cb: any) => cb(),
                        });

                        setConfirmPopup(true);
                        setPleaseWaitPopup(false);
                    });
            },
        });

        setDeletePopup(true);
    };

    return (
        <>
            <div className={`px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto`}>
                <div className="w-full bg-white p-4 rounded-md">
                    <h2 className={`text-lg font-semibold`}>Edit Service Type</h2>

                    <div className="mt-2 p-2">
                        <form onSubmit={(e) => handleSubmit(e)}>
                            <div className="w-full">
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-6">
                                    {/* First Column */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Service Name</label>
                                            <input
                                                type="text"
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                value={serviceName}
                                                placeholder="Service Name"
                                                onChange={(e) => setServiceName(e.target.value)}
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Price</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={0.01}
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                value={price}
                                                placeholder="Price"
                                                onChange={(e) => setPrice(validateMin(e.target.value, 0))}
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Taxable</label>
                                            <select
                                                className={`w-full px-[8px] py-[8px] border border-secondary rounded-md outline-none focus:border-primary text-sm bg-transparent`}
                                                value={isTaxable ? "Yes" : "No"}
                                                onChange={(e) => setIsTaxable(e.target.value === "Yes" ? true : false)}
                                            >
                                                <option value="Yes">Yes</option>
                                                <option value="No">No</option>

                                            </select>
                                        </div>


                                        <div ref={ref} className="relative w-full">
                                            <label className="block text-xs font-medium text-secondary">Icon</label>

                                            {/* Trigger */}
                                            <button
                                                type="button"
                                                onClick={() => setOpen(!open)}
                                                className="w-full flex items-center justify-between px-3 py-2 border border-secondary rounded-md bg-white text-sm"
                                            >
                                                {selected ? (
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-6 h-6">
                                                            {selected.icon}
                                                        </span>
                                                        <span>{selected.label}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">Select Icon</span>
                                                )}
                                                <span className="material-symbols-outlined text-gray-500">
                                                    expand_more
                                                </span>
                                            </button>

                                            {/* Dropdown */}
                                            {open && (
                                                <ul className="absolute z-50 mt-1 w-full bg-white border border-secondary rounded-md shadow-md max-h-60 overflow-auto">
                                                    {iconValues?.map((item: any, index: number) => (
                                                        <li
                                                            key={index}
                                                            onClick={() => {
                                                                setIcon(item.value);
                                                                setOpen(false);
                                                            }}
                                                            className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                                                        >
                                                            <span className="w-6 h-6">
                                                                {item.icon}
                                                            </span>
                                                            <span>{item.label}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Estimated Minutes</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={0}
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                placeholder="Estimated Minutes"
                                                value={estimatedMinutes}
                                                onChange={(e) => setEstimatedMinutes(validateMin(e.target.value, 0))}
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Description</label>
                                            <textarea
                                                rows={4}
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                value={description}
                                                placeholder="Description"
                                                onChange={(e) => setDescription(e.target.value)}

                                            />
                                        </div>
                                    </div>

                                    {/* Second Column */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Service Number</label>
                                            <input
                                                type="text"
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                value={serviceNumber}
                                                placeholder="Service Number"
                                                onChange={(e) => setServiceNumber(e.target.value)}
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Requires Tire Count</label>
                                            <select
                                                className={`w-full px-[8px] py-[8px] border border-secondary rounded-md outline-none focus:border-primary text-sm bg-transparent`}
                                                value={requiresTireCount}
                                                onChange={(e) => setRequiresTireCount(e.target.value)}
                                            >
                                                <option value="Yes">Yes</option>
                                                <option value="No">No</option>

                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Service Tax Rate</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={0.01}
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                value={serviceTaxRate}
                                                placeholder="Service Tax Rate"
                                                onChange={(e) => setServiceTaxRate(validateMin(e.target.value, 0))}
                                                required
                                            />
                                        </div>



                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Inventory Type</label>
                                            <select
                                                className={`w-full px-[8px] py-[8px] border border-secondary rounded-md outline-none focus:border-primary text-sm bg-transparent`}
                                                value={inventoryType}
                                                onChange={(e) => setInventoryType(Number(e.target.value))}
                                            >
                                                {
                                                    inventoryTypes?.map((item: any, index: number) => (
                                                        <option key={index} value={item?.id}>{item?.label}</option>
                                                    ))
                                                }


                                            </select>
                                        </div>

                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="flex flex-col sm:flex-row gap-4 mt-6">
                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-primary text-white cursor-pointer"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-[#FFFFFF] text-primary cursor-pointer border border-primary"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => deletingType(idNumber?.state)}
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-red-500 text-white cursor-pointer border border-red-600"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <PleasewaitPopup showPopup={pleaseWaitPopup} />
        </>
    )
}